import { copyProfileSections, createApplicationDocument } from "./applicationDocument.js";

const profileShareFormat = "regulator-profile";
const profileShareVersion = 1;

export function createProfileShare(profile, sections, now = new Date().toISOString()) {
  assertSections(sections);
  if (!profile?.name || !profile?.data) throw new Error("Select a profile to export.");
  const data = copyProfileSections(profile.data, sections);
  createApplicationDocument(data);
  return {
    format: profileShareFormat,
    version: profileShareVersion,
    exportedAt: now,
    profile: {
      name: profile.name,
      sections: normalizeSections(sections),
      data
    }
  };
}

export function parseProfileShare(payload) {
  if (payload?.format !== profileShareFormat || payload?.version !== profileShareVersion) {
    throw new Error("This is not a supported Regulator profile export.");
  }
  if (!String(payload.profile?.name ?? "").trim()) throw new Error("The exported profile has no name.");
  if (!["configuration", "allocation", "browserViews"].every((key) => typeof payload.profile.sections?.[key] === "boolean")) {
    throw new Error("The exported profile has invalid section selections.");
  }
  assertSections(payload.profile.sections);
  createApplicationDocument(payload.profile.data);
  return structuredClone(payload);
}

export function profileDataForSections(share, sections) {
  assertSections(sections);
  const selected = normalizeSections(sections);
  for (const key of Object.keys(selected)) {
    if (selected[key] && !share.profile.sections[key]) throw new Error(`The export does not include ${key}.`);
  }
  const data = copyProfileSections(share.profile.data, selected);
  createApplicationDocument(data);
  return data;
}

export function uniqueImportedProfileName(profiles, sourceName) {
  const base = `${String(sourceName).trim()} (Imported)`;
  const names = new Set(profiles.map((profile) => profile.name.trim().toLocaleLowerCase()));
  if (!names.has(base.toLocaleLowerCase())) return base;
  let number = 2;
  while (names.has(`${String(sourceName).trim()} (Imported ${number})`.toLocaleLowerCase())) number += 1;
  return `${String(sourceName).trim()} (Imported ${number})`;
}

export function profileShareSummary(share) {
  const data = share.profile.data;
  return {
    properties: data.properties.length,
    assets: data.assets.length,
    policies: (data.policies ?? []).length,
    allocations: (data.allocations ?? []).length,
    browserViews: (data.browserViews ?? []).length
  };
}

function normalizeSections(sections) {
  return {
    configuration: Boolean(sections.configuration),
    allocation: Boolean(sections.allocation),
    browserViews: Boolean(sections.browserViews)
  };
}

function assertSections(sections) {
  const normalized = normalizeSections(sections ?? {});
  if (!Object.values(normalized).some(Boolean)) throw new Error("Select at least one section.");
  if ((normalized.allocation || normalized.browserViews) && !normalized.configuration) {
    throw new Error("Allocation and Browser views require Configuration.");
  }
}
